import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Loader2, CheckCircle2, AlertTriangle, ArrowLeft, Info,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../lib/api';
import { toast } from 'sonner';

const PERSONA_SDK_URL = 'https://cdn.withpersona.com/dist/persona-v5.1.3.js';
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 90000; // 90s max after onComplete before we just show "pending review"

function loadPersonaSdk() {
  return new Promise((resolve, reject) => {
    if (window.Persona && window.Persona.Client) return resolve(window.Persona);
    const existing = document.getElementById('persona-sdk');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Persona));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.id = 'persona-sdk';
    s.src = PERSONA_SDK_URL;
    s.async = true;
    s.onload = () => resolve(window.Persona);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

const STATUS_META = {
  unverified:    { icon: ShieldCheck,  tone: 'slate',   label: 'Not yet verified' },
  pending:       { icon: Loader2,      tone: 'blue',    label: 'Verification in progress' },
  pending_review:{ icon: Info,         tone: 'amber',   label: 'Pending manual review' },
  approved:      { icon: CheckCircle2, tone: 'emerald', label: 'Identity verified' },
  declined:      { icon: AlertTriangle,tone: 'red',     label: 'Verification declined' },
  expired:       { icon: AlertTriangle,tone: 'red',     label: 'Verification expired' },
};

export default function VerifyIdentityPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const [waitingWebhook, setWaitingWebhook] = useState(false);
  const clientRef = useRef(null);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    fetchStatus().finally(() => setLoading(false));
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      try { clientRef.current && clientRef.current.destroy && clientRef.current.destroy(); } catch (_) { /* noop */ }
    };
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/persona/status');
      setStatus(res.data);
      return res.data;
    } catch (e) {
      console.error('KYC status fetch failed', e);
      return null;
    }
  };

  /** Poll status until approved/declined or timeout. */
  const pollUntilTerminal = (startedAt = Date.now()) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(async () => {
      const s = await fetchStatus();
      const terminal = s && ['approved', 'declined', 'expired'].includes(s.kyc_status);
      const manualReview = s && s.kyc_status === 'pending_review';
      const elapsed = Date.now() - startedAt;
      if (terminal || manualReview || elapsed > POLL_MAX_MS) {
        setWaitingWebhook(false);
        if (s?.kyc_status === 'approved') toast.success('Identity verified — you can proceed');
        else if (s?.kyc_status === 'declined') toast.error('Verification declined');
        else if (s?.kyc_status === 'pending_review') toast.info('Submitted for manual review');
        return;
      }
      pollUntilTerminal(startedAt);
    }, POLL_INTERVAL_MS);
  };

  const startFlow = async () => {
    setStarting(true);
    try {
      const { data } = await api.post('/persona/start');
      const persona = await loadPersonaSdk();

      const client = new persona.Client({
        inquiryId: data.inquiry_id,
        environmentId: data.environment_id || undefined,
        onReady: () => {
          setFlowOpen(true);
          client.open();
        },
        onComplete: ({ inquiryId, status: personaStatus }) => {
          setFlowOpen(false);
          setWaitingWebhook(true);
          toast.info('Submission received — confirming with CorpCraft…');
          pollUntilTerminal();
        },
        onCancel: () => {
          setFlowOpen(false);
          toast.info('Verification cancelled');
        },
        onError: (err) => {
          setFlowOpen(false);
          console.error('Persona error:', err);
          toast.error('Verification widget failed to load. Please try again.');
        },
      });
      clientRef.current = client;
    } catch (e) {
      console.error('Start KYC error:', e);
      toast.error(e.response?.data?.detail || 'Could not start verification');
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
        </div>
      </DashboardLayout>
    );
  }

  const current = status?.kyc_status || 'unverified';
  const meta = STATUS_META[current] || STATUS_META.unverified;
  const Icon = meta.icon;
  const approved = current === 'approved';

  const toneClasses = {
    slate:   'bg-slate-800/40 border-slate-700 text-slate-300',
    blue:    'bg-blue-500/10 border-blue-500/40 text-blue-200',
    amber:   'bg-amber-500/10 border-amber-500/40 text-amber-200',
    emerald: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200',
    red:     'bg-red-500/10 border-red-500/40 text-red-200',
  }[meta.tone];

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="verify-page">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6"
          data-testid="verify-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold font-['Outfit']">Identity Verification</h1>
          <p className="text-slate-400 mt-2">
            CorpCraft uses Persona to verify your identity before activating paid services.
            Your documents are encrypted and never stored on our servers.
          </p>
        </div>

        {/* Status banner */}
        <div
          className={`rounded-xl border p-5 flex items-center gap-4 mb-6 ${toneClasses}`}
          data-testid="kyc-status-banner"
        >
          <Icon className={`w-6 h-6 shrink-0 ${current === 'pending' ? 'animate-spin' : ''}`} />
          <div className="flex-1">
            <div className="font-semibold">{meta.label}</div>
            <div className="text-xs opacity-80 mt-0.5">
              {status?.is_sandbox && <span className="mr-2 font-mono">SANDBOX</span>}
              {status?.inquiry_id && <span className="font-mono">Inquiry: {status.inquiry_id}</span>}
            </div>
          </div>
          {approved && (
            <Button onClick={() => navigate('/dashboard')} className="gradient-accent" data-testid="kyc-done-btn">
              Continue
            </Button>
          )}
        </div>

        {/* Main action card */}
        {!approved && (
          <div className="stat-card" data-testid="verify-action-card">
            {waitingWebhook ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-3" />
                <div className="font-semibold">Finalising your verification…</div>
                <div className="text-sm text-slate-400 mt-1">
                  We're waiting for Persona to confirm the result. This usually takes a few seconds.
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-semibold mb-2">Start verification</h2>
                <p className="text-sm text-slate-400 mb-5">
                  Click below to launch Persona's secure widget. You'll need a valid government-issued ID
                  and a webcam or phone camera for the selfie step.
                </p>

                <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 mb-5 space-y-2 text-sm text-slate-300">
                  <div className="flex items-start gap-2">
                    <span className="text-blue-400 font-mono text-xs mt-0.5">1</span>
                    <span>Capture a photo of your government-issued ID (driver's license, passport, etc.)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-blue-400 font-mono text-xs mt-0.5">2</span>
                    <span>Take a quick selfie so Persona can match it to your ID</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-blue-400 font-mono text-xs mt-0.5">3</span>
                    <span>We'll receive the result automatically — no need to wait</span>
                  </div>
                </div>

                <Button
                  onClick={startFlow}
                  disabled={starting || flowOpen}
                  className="gradient-accent w-full sm:w-auto"
                  data-testid="start-kyc-btn"
                >
                  {starting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</>
                  ) : flowOpen ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verification open</>
                  ) : (
                    <><ShieldCheck className="w-4 h-4 mr-2" /> Start verification</>
                  )}
                </Button>

                {status?.is_sandbox && (
                  <p className="text-xs text-slate-500 mt-4">
                    <Info className="w-3 h-3 inline mr-1" />
                    Sandbox mode — you can use Persona's test data (any dummy ID image + any selfie works).
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {approved && (
          <div className="stat-card" data-testid="verify-complete-card">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              <h2 className="text-xl font-semibold">You're all set</h2>
            </div>
            <p className="text-sm text-slate-400">
              Your identity has been verified. You can now purchase formation packages, file state filings,
              and use any paid feature on CorpCraft.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
